import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Lock, Sparkles } from 'lucide-react';
import {
  FEATURES,
  LIMITS,
  cheapestPlanWith,
  formatMoney,
  hasAnyPermission,
  hasPermission,
  type FeatureKey,
  type LimitKey,
  type PermissionKey,
  type PlanDTO,
} from '@dezza/shared';
import { Button, Card, Sheet } from '@dezza/ui';
import { api, apiEvents } from './api';
import { useAuth } from './auth';

// ---------------------------------------------------------------- Permissões
export function useCan(perm: PermissionKey | PermissionKey[], mode: 'all' | 'any' = 'all'): boolean {
  const { me } = useAuth();
  const perms = me?.membership?.permissions;
  if (!perms) return false;
  if (Array.isArray(perm) && mode === 'any') return hasAnyPermission(perms, perm);
  return hasPermission(perms, perm);
}

export function Can({ perm, children, fallback = null, mode }: { perm: PermissionKey | PermissionKey[]; children: ReactNode; fallback?: ReactNode; mode?: 'all' | 'any' }) {
  return useCan(perm, mode) ? <>{children}</> : <>{fallback}</>;
}

// ---------------------------------------------------------------- Recursos do plano
export function useFeature(feature: FeatureKey): boolean {
  const { me } = useAuth();
  return Boolean(me?.entitlements?.features[feature]);
}

/** Feature flag do superadmin (liberação gradual): `useFlag('novo-checkout')`. */
export function useFlag(key: string): boolean {
  const { me } = useAuth();
  return me?.flags?.includes(key) ?? false;
}

export function useLimit(limit: LimitKey): { limit: number | null; used: number; reached: boolean } {
  const { me } = useAuth();
  const value = me?.entitlements?.limits[limit] ?? null;
  const usageMap: Partial<Record<LimitKey, number>> = {
    products: me?.usage?.products,
    users: me?.usage?.users,
    activeCoupons: me?.usage?.activeCoupons,
    catalogOrdersPerMonth: me?.usage?.catalogOrdersThisMonth,
    aiCreditsPerMonth: me?.usage?.aiCreditsThisMonth,
    storageMb: me?.usage?.storageMb,
  };
  const used = usageMap[limit] ?? 0;
  return { limit: value, used, reached: value !== null && used >= value };
}

// ---------------------------------------------------------------- Upsell (modal de upgrade)
interface UpgradeRequest {
  feature?: FeatureKey;
  limit?: LimitKey;
  message?: string;
}

const UpgradeContext = createContext<(req: UpgradeRequest) => void>(() => undefined);

export function usePlans() {
  return useQuery({
    queryKey: ['plans'],
    queryFn: () => api.get<PlanDTO[]>('/public/plans'),
    staleTime: 10 * 60_000,
  });
}

export function UpgradeProvider({ children }: { children: ReactNode }) {
  const [req, setReq] = useState<UpgradeRequest | null>(null);
  const navigate = useNavigate();
  const { data: plans } = usePlans();
  const { me } = useAuth();

  const open = useCallback((r: UpgradeRequest) => setReq(r), []);
  useEffect(() => {
    const onUpgrade = (e: Event) => setReq((e as CustomEvent<UpgradeRequest>).detail ?? {});
    apiEvents.addEventListener('upgrade', onUpgrade);
    return () => apiEvents.removeEventListener('upgrade', onUpgrade);
  }, []);

  const plan = req?.feature && plans ? cheapestPlanWith(plans, req.feature) : null;
  const title = req?.feature
    ? `${FEATURES[req.feature].label}`
    : req?.limit
      ? `Limite do plano: ${LIMITS[req.limit].label.toLowerCase()}`
      : 'Faça upgrade do seu plano';
  const canManageBilling = me?.membership?.permissions.includes('billing.manage');

  return (
    <UpgradeContext.Provider value={open}>
      {children}
      <Sheet
        open={Boolean(req)}
        onOpenChange={(o) => !o && setReq(null)}
        title={title}
        size="sm"
        footer={
          <>
            <Button variant="outline" block onClick={() => setReq(null)}>
              Agora não
            </Button>
            {canManageBilling ? (
              <Button
                block
                leftIcon={<Sparkles />}
                onClick={() => {
                  setReq(null);
                  navigate('/configuracoes/plano');
                }}
              >
                Ver planos
              </Button>
            ) : null}
          </>
        }
      >
        <div className="flex flex-col items-center gap-3 pb-2 text-center">
          <span className="flex size-14 items-center justify-center rounded-2xl bg-gold-soft text-warning">
            <Lock className="size-7" />
          </span>
          <p className="text-[15px] leading-relaxed text-ink-2">
            {req?.message && req.limit
              ? req.message
              : plan
                ? `Este recurso está disponível a partir do plano ${plan.name} (${plan.priceMonthlyCents ? `${formatMoney(plan.priceMonthlyCents)}/mês` : 'grátis'}).`
                : req?.message ?? 'Este recurso não está disponível no seu plano atual.'}
          </p>
          {!canManageBilling ? <p className="text-sm text-ink-3">Peça ao responsável pela loja para atualizar o plano.</p> : null}
        </div>
      </Sheet>
    </UpgradeContext.Provider>
  );
}

export function useUpgrade() {
  return useContext(UpgradeContext);
}

/**
 * Mostra o conteúdo se o plano tiver o recurso; senão, um cartão de upsell
 * explicando o benefício (padrão "feature discovery", nunca uma tela quebrada).
 */
export function FeatureGate({
  feature,
  children,
  title,
  description,
  compact,
}: {
  feature: FeatureKey;
  children: ReactNode;
  title?: string;
  description?: string;
  compact?: boolean;
}) {
  const enabled = useFeature(feature);
  const upgrade = useUpgrade();
  if (enabled) return <>{children}</>;
  return (
    <Card className={compact ? 'p-4' : 'mx-auto my-6 max-w-lg p-6 text-center'}>
      <div className={compact ? 'flex items-center gap-3' : 'flex flex-col items-center gap-3'}>
        <span className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-gold-soft text-warning">
          <Sparkles className="size-6" />
        </span>
        <div className={compact ? 'min-w-0 flex-1' : ''}>
          <h3 className="text-base font-semibold">{title ?? FEATURES[feature].label}</h3>
          <p className="mt-1 text-sm text-ink-2">{description ?? 'Disponível em planos superiores. Libere agora e venda mais.'}</p>
        </div>
        <Button size={compact ? 'sm' : 'md'} onClick={() => upgrade({ feature })}>
          Conhecer
        </Button>
      </div>
    </Card>
  );
}
